/**
 * Pack Selection Interface Component
 * Supports Sequential, Choice, and Hybrid pack selection modes
 * FIXED: Uses DatabaseAPI functions instead of direct fetch calls
 */
import React, { useState, useEffect } from 'react';
import './PackSelectionInterface.css';
import PackPreview from './PackPreview';
import DraftProgressTracker from './DraftProgressTracker';
import { PackRecommendationEngine } from '../utils/PackRecommendationEngine';
import { getDraftStatus } from '../../DatabaseAPI';
import { getAvailablePackChoices, selectPackChoice } from '../../DatabaseAPI';

const PackSelectionInterface = ({ teamId, draftState, catalog, onPackSelected }) => {
    const [availablePacks, setAvailablePacks] = useState([]);
    const [selectedPack, setSelectedPack] = useState(null);
    const [recommendations, setRecommendations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [teamNeeds, setTeamNeeds] = useState([]);
    const [filterBy, setFilterBy] = useState('all');
    
    // Re-fetch the pack choices whenever the team completes another pack.
    // Field name is total_packs_completed (TeamDraftStateSerializer.Meta.fields,
    // pack_serializers.py:205) — `packs_completed` is always undefined, which made
    // this dependency a constant and stopped the list from refreshing between packs.
    useEffect(() => {
        loadAvailablePacks();
    }, [teamId, draftState?.total_packs_completed]);
    
    useEffect(() => {
        if (availablePacks.length > 0 && draftState) {
            const enhanced = PackRecommendationEngine.getRecommendations(
                availablePacks, 
                draftState,
                {} // User preferences - could be expanded later
            );
            setRecommendations(enhanced);
            
            // Get team needs summary
            const needs = PackRecommendationEngine.getTeamNeedsSummary(draftState);
            setTeamNeeds(needs);
        }
    }, [availablePacks, draftState]);
    
    const loadAvailablePacks = async () => {
        try {
            setLoading(true);
            setError(null);
            
            // FIXED: Use DatabaseAPI function instead of direct fetch
            const data = await getAvailablePackChoices(teamId);
            
            if (!data.available_packs || !Array.isArray(data.available_packs)) {
                throw new Error("❌ INVALID RESPONSE: No available_packs array received from server");
            }
            
            setAvailablePacks(data.available_packs);
            
        } catch (err) {
            console.error('Failed to load available packs:', err);
            setError(`Failed to load available packs: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };
    
    const selectPack = async (pack) => {
        if (selectedPack) return; // Prevent double-selection
        
        try {
            setSelectedPack(pack);
            setError(null);
            
            // FIXED: Use DatabaseAPI function instead of direct fetch
            if (!pack.pack_config_id) {
                throw new Error("❌ INVALID PACK DATA: pack_config_id is required for pack selection");
            }
            
            const packContents = await selectPackChoice(
                teamId,
                pack.pack_config_id,
                pack.sequence_id
            );
            
            if (!packContents.success || !packContents.pack) {
                throw new Error("❌ PACK SELECTION FAILED: Invalid response from server");
            }
            
            // Call the parent callback with pack and contents
            if (onPackSelected) {
                onPackSelected(pack, packContents);
            }
            
        } catch (err) {
            console.error('Pack selection failed:', err);
            setError(`Pack selection failed: ${err.message}`);
            setSelectedPack(null);
        }
    };
    
    const getFilteredPacks = () => {
        if (filterBy === 'all') return recommendations;
        
        return PackRecommendationEngine.filterPacksByNeed(recommendations, draftState, filterBy);
    };
    
    const renderPackSelection = () => {
        if (!catalog) return null;
        
        const selectionMode = catalog.pack_selection_mode || 'SEQUENTIAL';
        
        switch (selectionMode) {
            case 'SEQUENTIAL':
                return renderSequentialMode();
            case 'CHOICE':
                return renderChoiceMode();
            case 'HYBRID':
                return renderHybridMode();
            default:
                return renderSequentialMode();
        }
    };
    
    const renderSequentialMode = () => {
        const nextPack = recommendations[0]; // Only one pack available
        
        return (
            <div className="pack-sequential-interface">
                <div className="sequential-header">
                    <h3>Next Pack</h3>
                    <p>Complete packs in order to build your team</p>
                </div>
                
                {nextPack && (
                    <div className="next-pack-container">
                        <PackPreview 
                            pack={nextPack}
                            recommendation={nextPack}
                            selectable={!selectedPack}
                            onSelect={() => selectPack(nextPack)}
                            showRecommendation={true}
                        />
                    </div>
                )}
                
                {!nextPack && !loading && (
                    <div className="no-packs">
                        No more packs available for sequential selection
                    </div>
                )}
            </div>
        );
    };
    
    const renderChoiceMode = () => {
        const filteredPacks = getFilteredPacks();
        
        return (
            <div className="pack-choice-interface">
                <div className="choice-header">
                    <h3>Choose Your Next Pack</h3>
                    <p>Select from the available pack options</p>
                    
                    {/* Team needs summary */}
                    {teamNeeds.length > 0 && (
                        <div className="team-needs">
                            <strong>Team Needs: </strong>
                            {teamNeeds.slice(0, 2).map(need => 
                                `${need.position} (${need.needed})`
                            ).join(', ')}
                            {teamNeeds.length > 2 && '...'}
                        </div>
                    )}
                </div>
                
                {/* Filter options */}
                <div className="pack-filters">
                    <button 
                        className={`filter-btn ${filterBy === 'all' ? 'active' : ''}`}
                        onClick={() => setFilterBy('all')}
                    >
                        All Packs ({recommendations.length})
                    </button>
                    {teamNeeds.map(need => (
                        <button 
                            key={need.position}
                            className={`filter-btn ${filterBy === need.position.toLowerCase() ? 'active' : ''}`}
                            onClick={() => setFilterBy(need.position.toLowerCase())}
                        >
                            {need.position} Need
                        </button>
                    ))}
                    <button 
                        className={`filter-btn ${filterBy === 'legendary' ? 'active' : ''}`}
                        onClick={() => setFilterBy('legendary')}
                    >
                        Legendary
                    </button>
                </div>
                
                {/* Pack grid */}
                <div className="available-packs-grid">
                    {filteredPacks.map(pack => (
                        <PackPreview
                            key={pack.id || pack.pack_id}
                            pack={pack}
                            recommendation={pack}
                            selectable={!selectedPack}
                            onSelect={() => selectPack(pack)}
                            showRecommendation={true}
                        />
                    ))}
                </div>
                
                {filteredPacks.length === 0 && !loading && (
                    <div className="no-packs">
                        No packs available matching the selected filter
                    </div>
                )}
            </div>
        );
    };
    
    const renderHybridMode = () => {
        // Hybrid mode combines elements of both sequential and choice
        const hasChoices = recommendations.length > 1;
        
        if (hasChoices) {
            return (
                <div className="pack-hybrid-interface">
                    <div className="hybrid-header">
                        <h3>Pack Selection Phase</h3>
                        <p>Choose from available options in this phase</p>
                        <div className="hybrid-mode-indicator">
                            <span className="mode-label">CHOICE MODE</span>
                            <span className="phase-info">Multiple options available</span>
                        </div>
                    </div>
                    {renderChoiceMode()}
                </div>
            );
        } else {
            return (
                <div className="pack-hybrid-interface">
                    <div className="hybrid-header">
                        <h3>Required Pack</h3>
                        <p>This pack must be completed before advancing</p>
                        <div className="hybrid-mode-indicator">
                            <span className="mode-label">SEQUENTIAL MODE</span>
                            <span className="phase-info">Fixed progression</span>
                        </div>
                    </div>
                    {renderSequentialMode()}
                </div>
            );
        }
    };
    
    if (loading) {
        return (
            <div className="pack-selection-loading">
                <div className="loading-spinner"></div>
                <p>Loading pack options...</p>
            </div>
        );
    }
    
    if (error) {
        return (
            <div className="pack-selection-error">
                <div className="error-icon">⚠️</div>
                <p>Error: {error}</p>
                <button onClick={loadAvailablePacks} className="retry-btn">
                    Retry
                </button>
            </div>
        );
    }
    
    return (
        <div className="pack-selection-interface">
            {/* Progress tracker at the top */}
            <DraftProgressTracker 
                teamState={draftState}
                availablePacks={availablePacks}
                catalog={catalog}
                compact={true}
            />
            
            {/* Main pack selection area */}
            {renderPackSelection()}
            
            {/* Selection status */}
            {selectedPack && (
                <div className="selection-status">
                    <div className="status-content">
                        <div className="loading-spinner small"></div>
                        <p>Selected: <strong>{selectedPack.display_name || selectedPack.name}</strong></p>
                        <p className="status-subtext">Opening pack...</p>
                    </div>
                </div>
            )}
            
            {/* Debug info (hidden in production) */}
            {process.env.NODE_ENV === 'development' && (
                <div className="debug-info" style={{ display: 'none' }}>
                    <div>Mode: {catalog?.pack_selection_mode}</div>
                    <div>Available Packs: {availablePacks.length}</div>
                    <div>Team ID: {teamId}</div>
                </div>
            )}
        </div>
    );
};

export default PackSelectionInterface;